import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BodyCopy } from "../components/BodyCopy";
import {
  Button,
  Layout,
  SupportIcon,
} from "lola-framework-ui-test";
import { AuraLayout } from "../components/AuraLayout";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useExitToPartnerOrLanding } from "../hooks/useExitToPartnerOrLanding";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import {
  useRequestSSE,
  upsertRequestInList,
} from "../hooks/useRequestSSE";
import { useComposeStore } from "../store/useComposeStore";
import { listRequests } from "../api/requests";
import type { RequestSummary } from "../api/requests";
import { amountFormatter, getCurrentDateFormatted } from "../utils";
import { listRowReceiveAmount } from "../utils/requestListAmount";
import { getDisplayStatus } from "../utils/displayStatus";
import type { ITransaction } from "../interfaces/transactions";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { NotificationsBell } from "../components/notifications/NotificationsBell";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import { Navbar } from "../components/Navbar";

/** Pull distance (px) required to trigger a refresh on mobile. */
const PULL_THRESHOLD = 70;

const formatDate = (isoDate?: string): string => {
  if (!isoDate) return getCurrentDateFormatted();
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return getCurrentDateFormatted();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

/**
 * Requests hub. Lists the user's existing money requests (most recent first)
 * with live SSE updates and pull-to-refresh, and offers a primary action to
 * compose a new one.
 */
export const HomePage = memo(() => {
  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("home"));
  }, []);

  const { t } = useTranslation();
  const { theme, setAmountState } = useStore();
  const { session } = useSessionStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const { exitToPartnerOrLanding, canExitToPartner } =
    useExitToPartnerOrLanding();
  const { resetCompose } = useComposeStore();

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;

  const linkId = linkIdFromRoute || session.data?.linkId;

  // Blocked / suspended users keep read access to their history but cannot
  // originate new requests — the mint is rejected server-side, so surface it
  // here instead of letting them compose a request that cannot be created.
  const userStatus = session.data?.userStatus;
  const canRequest = !userStatus?.blocked && !userStatus?.suspended;

  const [requests, setRequests] = useState<RequestSummary[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);

  const mapRequest = useCallback(
    (r: RequestSummary): ITransaction => {
      const display = getDisplayStatus(r.status, r.operation?.status);
      return {
        requestId: r.requestId,
        operationId: r.operationId,
        status: r.status,
        statusKey: display.statusKey,
        tone: display.tone,
        amount: amountFormatter(
          listRowReceiveAmount(r),
          r.corridor.destinationCurrency
        ),
        currency: r.corridor.destinationCurrency,
        date: formatDate(r.createdAt),
        payerName: r.payerName,
        onSelect: r.requestId
          ? () => redirectTo(`${APP_ROUTES.RECEIPT_PAGE}/${r.requestId}`)
          : undefined,
      };
    },
    [redirectTo]
  );

  const transactions = useMemo<ITransaction[]>(
    () => requests.map(mapRequest),
    [requests, mapRequest]
  );

  const loadRequests = useCallback(async () => {
    if (!linkId || loadingRef.current) return;

    loadingRef.current = true;
    try {
      const items = await listRequests(linkId);
      setRequests(items);
    } catch (error) {
      console.error("[HomePage] Failed to fetch requests:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [linkId]);

  const refresh = useCallback(async () => {
    await loadRequests();
  }, [loadRequests]);

  const upsertRequest = useCallback((req: RequestSummary) => {
    setRequests((prev) => upsertRequestInList(prev, req));
  }, []);

  const reconcileFromServer = useCallback(async () => {
    if (!linkId) return;
    try {
      const items = await listRequests(linkId);
      setRequests((prev) =>
        items.reduce((acc, req) => upsertRequestInList(acc, req), prev)
      );
    } catch (error) {
      console.error("[HomePage] SSE reconnect reconcile failed:", error);
    }
  }, [linkId]);

  useRequestSSE(linkId, requests, upsertRequest, reconcileFromServer);

  const { pullDistance, refreshing, pulling } = usePullToRefresh(
    containerRef,
    refresh,
    { threshold: PULL_THRESHOLD }
  );

  useEffect(() => {
    if (linkId) {
      void loadRequests();
    }
  }, [linkId, loadRequests]);

  const handleNewRequest = useCallback(() => {
    if (!canRequest) return;
    resetCompose();
    // QuoteInfo seeds from global amountState — clear so a fresh compose
    // never reuses the previous request's amount (same as send flow).
    setAmountState({ amount: "" });
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  }, [canRequest, resetCompose, setAmountState, redirectTo]);

  const { openSupport, supportSheet } = useSupportSheet();
  const inbox = useNovuInbox();
  const headerSlot = inbox?.headerSlot ?? "help";
  const showBell = headerSlot === "bell";
  const showHelp = headerSlot === "help";

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={
          showBell
            ? "need-help-header notifications-home-header"
            : "need-help-header"
        }
      >
        <Layout.Header>
          {headerSlot === "pending" ? null : (
            <Navbar
              title={showHelp ? t("common.needHelp") : undefined}
              align="right"
              onBackClick={() => redirectTo(APP_ROUTES.LANDING_PAGE)}
              onIconClick={showHelp ? () => openSupport() : undefined}
              icon={
                showBell ? (
                  <NotificationsBell />
                ) : (
                  <SupportIcon size={35} colors={dropzoneColors} />
                )
              }
            />
          )}
        </Layout.Header>
        <Layout.Content className="transactions-section">
          <TransactionsHistory
            items={transactions}
            containerRef={containerRef}
            pullDistance={pullDistance}
            refreshing={refreshing}
            pulling={pulling}
            pullThreshold={PULL_THRESHOLD}
          />
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {!canRequest && (
              <BodyCopy
                textColor={bodyCopy}
                className="transactions-disabled-note"
              >
                {t("transactions.requestsDisabled")}
              </BodyCopy>
            )}
            {canExitToPartner && (
              <Button
                size="small"
                variant="cancel"
                background={backgroundBtn}
                color={backgroundBtn}
                className="alreary-acc-btn"
                onClick={exitToPartnerOrLanding}
              >
                {t("transactions.backHome")}
              </Button>
            )}
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={!canRequest}
              onClick={handleNewRequest}
            >
              {t("transactions.newRequest")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
